import { useCallback, useEffect, useRef, useState } from 'react';
import { apiGet } from '../client';

export interface PlanLimits {
    max_note_size_bytes: number;
    max_text_storage_bytes: number;
    max_file_size_bytes: number;
    max_file_storage_bytes: number;
    max_versions_per_note: number;
}

export interface PlanUsageData {
    text_storage_bytes: number;
    file_storage_bytes: number;
}

export interface UsageResponse {
    limits: PlanLimits;
    usage: PlanUsageData;
}

export function useUsageData() {
    const [data, setData] = useState<UsageResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const ctrlRef = useRef<AbortController | null>(null);

    const refetch = useCallback(async () => {
        ctrlRef.current?.abort();
        const ctrl = new AbortController();
        ctrlRef.current = ctrl;

        setLoading(true);
        setError(null);

        try {
            const response = await apiGet<UsageResponse>('/app/v1/account/usage');
            setData(response);
        } catch (e: any) {
            if (e?.name === 'AbortError') return;
            setError(e?.message || 'Failed to load usage data');
        } finally {
            if (ctrlRef.current === ctrl) ctrlRef.current = null;
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void refetch();
        return () => ctrlRef.current?.abort();
    }, [refetch]);

    return { data, loading, error, refetch };
}
